import React, { useEffect, useRef, useState } from 'react';
import { Box, InputBase, IconButton, CircularProgress, Paper, Typography, makeStyles, Tooltip, Select, MenuItem, Menu } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import SendIcon from '@material-ui/icons/Send';
import { useSelector } from 'react-redux';
import { getAiAssistantContext } from '../../../store/global/selectors';
import ExpandMore from '@material-ui/icons/ExpandMore';
import OwnershipStatusLabel from '../../atoms/labels/OwnershipStatusLabel';
import StopIcon from '@material-ui/icons/Stop';

const useStyles = makeStyles((theme) => ({
  inputWrapper: {
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 16,
    padding: '8px',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 2px 10px rgba(0,0,0,0.03)',
    '&:focus-within': { borderColor: '#34445c' }
  },
  generatingInput: {
    borderRadius: "0px 0px 16px 16px",
  },
  input: {
    height: 'auto',
    overflowY: 'auto',
    maxHeight: '150px',
    display: 'block',
    resize: 'none',
    '&::-webkit-scrollbar': {
      width: 8,
      height: 8,
    },
    '&::-webkit-scrollbar-track': {
      boxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)',
      webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#ccc',
      borderRadius: '5px',
    },
    transition: 'height 0.5s ease',
  },
  assetArea: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
  },
  assetChip: {
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    padding: '4px 10px',
    display: 'flex',
    alignItems: 'center',
    border: '1px solid #e2e8f0',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#34445c',
  },
  suggestionsPaper: {
    position: 'absolute',
    bottom: '100%',
    left: 0,
    maxWidth: 350,
    marginBottom: theme.spacing(1),
    maxHeight: 200,
    overflowY: 'auto',
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    zIndex: 1000,
    '&::-webkit-scrollbar': {
      width: 8,
      height: 8,
    },
    '&::-webkit-scrollbar-track': {
      boxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)',
      webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#ccc',
      borderRadius: '5px',
    },
  },
  suggestionsPaperRight: {
    position: 'absolute',
    bottom: '100%',
    right: 0,
    maxWidth: 350,
    marginBottom: theme.spacing(1),
    maxHeight: 200,
    overflowY: 'auto',
    borderRadius: 12,
    boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    zIndex: 1000,
    '&::-webkit-scrollbar': {
      width: 8,
      height: 8,
    },
    '&::-webkit-scrollbar-track': {
      boxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)',
      webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#ccc',
      borderRadius: '5px',
    },
  },
  suggestionItem: {
    padding: '10px 16px',
    cursor: 'pointer',
    '&:hover': { backgroundColor: '#eef7ff', color: '#34445c' },
    fontSize: '0.85rem',
  },
  agentRunningBar: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    backgroundColor: '#f0fdf4',
    border: '1px solid #d2f9e0',
    borderBottom: 'none',
    borderRadius: "16px 16px 0px 0px",
    padding: '8px 14px',
  },
  pulseDot: {
    width: 8,
    height: 8,
    backgroundColor: '#10b981',
    borderRadius: '50%',
    marginRight: theme.spacing(1.5),
    boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.7)',
  },
  animatedPulseDot: {
    animation: '$pulse 2s infinite',
  },
  '@keyframes pulse': {
    '0%': { boxShadow: '0 0 0 0 rgba(16, 185, 129, 0.7)' },
    '70%': { boxShadow: '0 0 0 10px rgba(255, 234, 0, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(255, 234, 0, 0)' },
  },
  agentRunningText: {
    fontSize: '0.8rem',
    fontWeight: 500,
    color: '#15803d',
  },
  reviewRunningBar: {
    backgroundColor: '#fffbeb',
    border: '1px solid #fde68a',
    borderBottom: 'none',
  },
  reviewPulseDot: {
    backgroundColor: '#f59e0b',
    animation: '$pulseAmber 1.2s infinite',
  },
  reviewRunningText: {
    color: '#92400e',
    fontWeight: 600,
  },
  '@keyframes pulseAmber': {
    '0%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0.7)' },
    '70%': { boxShadow: '0 0 0 8px rgba(245, 158, 11, 0)' },
    '100%': { boxShadow: '0 0 0 0 rgba(245, 158, 11, 0)' },
  },
  dbTypeSelector: {
    display: 'flex',
    flexShrink: 0,
    padding: '4px 8px',
    borderRadius: 10,
    alignItems: 'center',
    cursor: 'pointer',
    fontSize: '0.75rem',
    fontWeight: 600,
    color: '#34445c',
    userSelect: 'none',
    whiteSpace: 'nowrap',
    transition: 'background-color 0.15s',
    '&:hover': { backgroundColor: '#e2e8f0' },
  },
  dbDropdownWrapper: {
    position: 'relative',
    flexShrink: 0
  },
  dbDropdownMenu: {
    position: 'absolute',
    bottom: '100%',
    right: 0,
    marginBottom: 8,
    width: 260,
    backgroundColor: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
    overflow: 'hidden',
    zIndex: 100,
    padding: '4px',
  },
  dbDropdownItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '8px 10px',
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'background 0.15s',
    '&:hover': {
      backgroundColor: '#f1f5f9',
    },
  },
  dbDropdownItemActive: {
    backgroundColor: '#f1f5f9',
  },
  dbDropdownItemIcon: {
    width: 28,
    height: 28,
    borderRadius: 8,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: '0.85rem',
    marginTop: 1,
  },
  dbDropdownItemTitle: {
    fontSize: '0.78rem',
    fontWeight: 600,
    color: '#1e293b',
    lineHeight: 1.3,
  },
  dbDropdownItemDesc: {
    fontSize: '0.68rem',
    color: '#94a3b8',
    lineHeight: 1.4,
    marginTop: 1,
  },
  sendButton: {
    outline: 'none',
    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
    marginBottom: 2,
  },
  stopButton: {
    backgroundColor: '#34445c',
    color: '#fff',
    borderRadius: '50%',
    width: 25,
    height: 25,
    transition: 'background-color 0.15s',
    '&:hover': {
      backgroundColor: '#1e293b',
      color: '#fff',
    },
    outline: 'none',

    '&:focus': {
      outline: 'none',
      boxShadow: 'none',
    },
    marginBottom: 2,
  },
  dbTypeSelectorDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
    pointerEvents: 'auto', 
    '&:hover': { backgroundColor: 'transparent' },
  }

}))

const entityTypeMap = {
  'database_profile': "Database Delegate",
  'transaction': "Transaction",
  'view': "View",
  'simulation': "Testsuite"
}

const dbActionOptions = [
  {
    value: 'schema',
    label: 'Database Schema',
    description: 'Define your database tables, columns, indexes, and relationships.',
    icon: <img src={`${process.env.PUBLIC_URL}/ide_icons/database_schema.svg`} width={18} height={18} />,
    iconBg: '#eef2f7',
  },
  {
    value: 'data',
    label: 'Sample Data',
    description: 'Auto-seeds realistic test records into your deployed app, so you can preview dashboards without entering data manually.',
    icon: <img src={`${process.env.PUBLIC_URL}/ide_icons/database_data.svg`} width={18} height={18} />,
    iconBg: '#eef2f7',
  },
];


const ChatInput = ({ isLoading, handlePromptSend, listTitle, entityList, disableAll, isAwaitingReview, isWatingForAnswer, summaryExist, isSystemModelEdit, onStopGeneration, dbActionType, setDbActionType }) => {
  const inputRef = useRef(null)
  const dbDropdownRef = useRef(null);

  const classes = useStyles();
  const [prompt, setPrompt] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState([]);
  const [mentionQuery, setMentionQuery] = useState('');
  const [disableButton, setDisableButton] = useState(false);
  const [disableInput, setDisableInput] = useState(false);
  const [buttonToolTip, setButtonTooltip] = useState('');
  const [placeholder, setPlaceHolder] = useState('Describe your application idea…')
  const [suggestionAnchor, setSuggestionAnchor] = useState('left');
  const [dbMenuOpen, setDbMenuOpen] = useState(false);



  const { entityType, entity, isCreate, action } = useSelector(getAiAssistantContext)

  const handleChange = (e) => {
    const value = e.target.value;
    setPrompt(value);

    // Match @word at the end (e.g. "@fle", "@air")
    const match = value.match(/@(\w*)$/);

    if (match && selectedEntity.length === 0) {
      setSuggestionAnchor('left');
      setShowSuggestions(true);
      setMentionQuery(match[1]); // text after "@"
    } else {
      setShowSuggestions(false);
      setMentionQuery('');
    }
  };



  const filteredEntities = entityList?.filter(entity =>
    entity.title.toLowerCase().includes(mentionQuery.toLowerCase())
  );

  const selectEntity = (entity, entityType) => {
    entity.entity_type = entityType;
    if (!selectedEntity.find(e => e.id === entity.id)) {
      setSelectedEntity([...selectedEntity, entity]);
    }

    // Replace @query with empty string
    setPrompt(prev => prev.replace(/@\w*$/, ''));

    setShowSuggestions(false);
    setMentionQuery('');
  };

  const removeEntity = (entity) => {
    setSelectedEntity(selectedEntity.filter(e => e.id !== entity.id));
  };

  // NEW: shared send handler
const submitPrompt = () => {
  if (disableAll || disableButton) return;
  setSelectedEntity([])
  setPrompt('')
  handlePromptSend({ prompt, entity: selectedEntity?.[0] })
}

// NEW: keydown handler for Enter / Shift+Enter
const handleKeyDown = (e) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault(); // stop the default newline insertion
    submitPrompt();
  }
  // Shift+Enter: do nothing special — let the default behavior insert a newline
};

  useEffect(() => {
    if (entity && (!isCreate || entityType === "database_profile") && !isLoading) {
      setSelectedEntity([{
        id: entity._id,
        name: entity.name,
        title: entity.name,
        entity_type: entityType
      }])
      if (entityType === "database_profile" && action) {
        setDbActionType(action);
      }
    }
  }, [entity, isCreate, entityType])

  useEffect(() => {
    if (selectedEntity?.[0] && selectedEntity?.[0]?.entity_type !== entityType) {
      setSelectedEntity([]);
      return;
    }

    if (!entityList?.length) return;
    if (selectedEntity?.length) {
      const entities = entityList?.map(entity => String(entity.id))
      if (!entities.includes(String(selectedEntity?.[0]?.id))) {
        setSelectedEntity([]);
      }
    }
  }, [entityList, entityType])

  useEffect(() => {
    if (entityType && inputRef.current) {
      inputRef.current.focus();
    }
  }, [entityType]);


  useEffect(() => {
    if (!dbMenuOpen) return;
    const handleClickOutside = (e) => {
      if (dbDropdownRef.current && !dbDropdownRef.current.contains(e.target)) {
        setDbMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dbMenuOpen]);

  useEffect(() => {
    setButtonTooltip('')
    if (isLoading) {
      setDisableInput(true)
    } else {
      setDisableInput(false);
    }
    switch (entityType) {
      case 'database_profile':
      case 'transaction':
      case 'view':
      case 'simulation':
        if (entityList.length && !selectedEntity?.length) {
          setPlaceHolder(`Type @ to select a ${entityTypeMap[entityType]}`)
        } else if (entityList.length && selectedEntity?.length) {
          setPlaceHolder(`Describe the action…`)
        } else {
          setPlaceHolder('')
        }
        if (selectedEntity?.length && (!prompt.trim() || isLoading)) {
          setDisableButton(true);
          if (!prompt.trim()) {
            setButtonTooltip('Prompt is required')
          }
        } else if (!selectedEntity?.length && isLoading) {
          setDisableButton(true);
        } else {
          setDisableButton(false);
        }
        if (entityType === "database_profile" && action === "data") {
          setDisableButton(false);
        }
        break;
      case 'system_model':
        if ((!prompt.trim() || isLoading) && (!summaryExist || isSystemModelEdit)) {
          setDisableButton(true);
          if (!prompt.trim() && !isLoading) {
            setButtonTooltip('Prompt is required')
          }
        } else {
          setDisableButton(false)
          setButtonTooltip('')
        }
        setPlaceHolder(`Describe your application idea…`)
        break;
      default:
        if ((!prompt.trim() || isLoading) && (!summaryExist || isSystemModelEdit)) {
          setDisableButton(true);
          if (!prompt.trim() && !isLoading) {
            setButtonTooltip('Prompt is required')
          }
        } else {
          setDisableButton(false)
          setButtonTooltip('')
        }
        setPlaceHolder(`Describe your application idea…`)
        break;
    }
  }, [isLoading, entityType, prompt, selectedEntity, entityList, summaryExist])


  useEffect(() => {
    if (selectedEntity.length && inputRef.current) {
      inputRef.current?.focus()
    }
  }, [selectedEntity])

  const onClickAtChip = () => {
    if (entityList?.length === 0) return
    setPrompt(prev => prev.endsWith('@') ? prev : prev + '@');
    setSuggestionAnchor('right');
    setShowSuggestions(true);
    setMentionQuery('');
    inputRef.current?.focus();
  }

  const isDbSelectorDisabled = isLoading || disableAll;

  return (
    <Box style={{ position: 'sticky', width: '100%', marginTop: 'auto', left: 0, bottom: '10px' }}>

      {/* 1. Suggestions List (Popover) */}
      {showSuggestions && filteredEntities?.length > 0 && (
        <Paper
          className={suggestionAnchor === 'right' ? classes.suggestionsPaperRight : classes.suggestionsPaper}
          elevation={3}>
          <Box p={1.5} borderBottom="1px solid #f1f5f9" style={{ padding: '5px 12px' }}>
            <Typography variant="overline" style={{ fontWeight: 800, color: '#94a3b8', lineHeight: 'initial' }}>{listTitle}</Typography>
          </Box>
          {filteredEntities?.length > 0 && filteredEntities.map((entity, i) => (
            <div key={entity.id} className={classes.suggestionItem} onClick={() => selectEntity(entity, entityType)}>
              <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                {entity.title}
                <OwnershipStatusLabel entity={entity} />
              </Box>
            </div>
          ))}
        </Paper>
      )}

      {(isLoading || isAwaitingReview || isWatingForAnswer) && (
        <Box className={`${classes.agentRunningBar} ${isAwaitingReview || isWatingForAnswer ? classes.reviewRunningBar : ''}`}>
          <div className={`${classes.pulseDot} ${isAwaitingReview || isWatingForAnswer ? classes.reviewPulseDot : classes.animatedPulseDot
            }`} />
          <Typography className={`${classes.agentRunningText} ${isAwaitingReview ? classes.reviewRunningText : ''}`}>
            {
              isWatingForAnswer ?
                'Wating for your answer - check the question above'
                : isAwaitingReview
                  ? 'Waiting for your review — check the list above'
                  : <>{'Agent is running'}<span style={{ paddingLeft: 2 }} className="dot-loader" /></>
            }
          </Typography>
        </Box>
      )}

      {/* 2. Main Input Container */}
      <Box className={`${classes.inputWrapper} ${(isLoading || isAwaitingReview || isWatingForAnswer) ? classes.generatingInput : ""}`}>

        {/* 3. Selected Assets Area (Like Gemini) */}
        {selectedEntity.length > 0 && (
          <Box className={classes.assetArea}>
            {selectedEntity.map((entity, i) => (
              <div key={entity.id} className={classes.assetChip}>
                @{entity?.title}
                <IconButton size="small" onClick={() => removeEntity(entity)} style={{ marginLeft: 4, padding: 2 }}>
                  <CloseIcon style={{ fontSize: 12 }} />
                </IconButton>
              </div>
            ))}
          </Box>
        )}

        {/* 4. Text Input & Send Button */}
        <Box display="flex" alignItems="flex-end">
          <InputBase
            inputRef={inputRef}
            disabled={disableAll || disableInput}
            fullWidth
            multiline
            value={prompt}
            autoFocus
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            placeholder={isLoading ? "Waiting for response..." : disableAll ? "An action is required to move forward" : placeholder}
            className={classes.input}
            style={{ paddingLeft: 8 }}
          />

          {entityType === 'database_profile' && (
            <Box className={classes.dbDropdownWrapper} ref={dbDropdownRef}>
              {dbMenuOpen && (
                <Box className={classes.dbDropdownMenu}>
                  {dbActionOptions.map((option) => (
                    <Box
                      key={option.value}
                      className={`${classes.dbDropdownItem} ${dbActionType === option.value ? classes.dbDropdownItemActive : ''}`}
                      onClick={() => {
                        setDbActionType(option.value);
                        setDbMenuOpen(false);
                      }}
                    >
                      <Box className={classes.dbDropdownItemIcon} style={{ backgroundColor: option.iconBg }}>
                        {option.icon}
                      </Box>
                      <Box>
                        <Typography className={classes.dbDropdownItemTitle}>
                          {option.label}
                        </Typography>
                        <Typography className={classes.dbDropdownItemDesc}>
                          {option.description}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}

              <Box 
                className={`${classes.dbTypeSelector} ${isDbSelectorDisabled ? classes.dbTypeSelectorDisabled : ''}`}
                onClick={() => {if(isDbSelectorDisabled){return;} setDbMenuOpen((prev) => !prev)}}
               >
                <Typography style={{ fontSize: '0.75rem', fontWeight: 600 }}>
                  {dbActionOptions.find(o => o.value === dbActionType)?.label}
                </Typography>
                <ExpandMore style={{ fontSize: 16, transform: dbMenuOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
              </Box>
            </Box>
          )}

          {['database_profile', 'transaction', 'view', 'simulation'].includes(entityType)
            && !selectedEntity.length
            && !disableAll
            && !showSuggestions
            && !isLoading
            && (
              <Tooltip title={entityList?.length ? `Pick a ${entityTypeMap[entityType] || 'entity'}` : `No ${entityTypeMap[entityType] || 'entity'} available`} placement="top" arrow>
                <span>
                  <IconButton
                    size="small"
                    disableRipple
                    disabled={!entityList?.length}
                    onClick={onClickAtChip}
                    style={{ color: entityList?.length ? '#64748b' : '#cbd5e1', marginRight: 5 }}
                    onMouseDown={(e) => e.preventDefault()} existingDbProfile
                  >
                    <Typography style={{ fontWeight: 800, fontSize: '1rem', lineHeight: 1 }}>@</Typography>
                  </IconButton>
                </span>
              </Tooltip>
            )}
          <Tooltip
            title={isLoading ? "Stop generation" : disableAll ? "An action is required to move forward" : buttonToolTip}
            placement="top"
            arrow
          >
            {/* Important: Tooltips don't trigger on disabled elements. 
              Wrapping in a span ensures the hover message shows up even if the button is disabled.
            */}
            <span>
              <IconButton
                size="small"
                disableRipple
                disableFocusRipple
                className={isLoading ? classes.stopButton : classes.sendButton}
                onClick={() => {
                  if (isLoading) {
                    onStopGeneration?.();
                    return;
                  }
                  setSelectedEntity([])
                  setPrompt('')
                  handlePromptSend({ prompt, entity: selectedEntity?.[0], dbActionType })
                }}
                disabled={!isLoading && (disableAll || disableButton || isWatingForAnswer)}
              >
                {isLoading ? (
                  <StopIcon style={{ fontSize: 16 }} />
                ) : (
                  <SendIcon style={{ fontSize: 18 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );
};

export default ChatInput;